import axiosInstance from 'plugins/axios';
import { IListUniqLinksPayload, IUniqLinkPayload } from 'types/uniq-link';
import { IBodyResponse } from 'types/type.global';
import { toast } from 'react-toastify';

export interface IMeta {
    total_count?: number;
    total_page?: number;
    page?: number;
    per_page?: number;
}

export interface IBodyResponseWithMeta<T> extends IBodyResponse<T> {
    meta?: IMeta;
    user_unique_links?: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    statistics?: {
        total: number;
        answered: number;
        not_answered: number;
    };
}

export const listUniqLinks = async (
    params: IListUniqLinksPayload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponseWithMeta<any>> => {
    const { survey_id, ...queryParams } = params;
    const endpoint = survey_id
        ? `admin/surveys/${survey_id}/user_unique_links`
        : 'admin/uniq_links';
    const response = await axiosInstance.get(endpoint, {
        params: queryParams,
    });
    return response.data;
};

export const listSecretCodes = async (
    params: {
        survey_id: number;
        page?: number;
        per_page?: number;
        status?: string;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponseWithMeta<any>> => {
    const { survey_id, ...queryParams } = params;
    const endpoint = `admin/surveys/${survey_id}/list_secret_code_once`;
    const response = await axiosInstance.get(endpoint, {
        params: queryParams,
    });
    return response.data;
};

export const createSecretCodes = async (
    surveyId: number,
    quantity: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.post(
            `admin/surveys/${surveyId}/create_secret_code_once`,
            {
                amount: quantity,
            }
        );
        if (response.status === 200) {
            toast.success(response.data.message || 'Tạo mã bảo mật thành công');
        } else {
            toast.error(response.data.message || 'Tạo mã bảo mật thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra khi tạo mã bảo mật');
        throw error;
    }
};

export const createUniqLink = async (
    data: IUniqLinkPayload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.post('admin/uniq_links', {
            uniq_link: data,
        });
        if (response.status === 200) {
            toast.success(response.data.message || 'Tạo thành công');
        } else {
            toast.error(response.data.message || 'Tạo thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra');
        throw error;
    }
};

export const updateUniqLink = async (
    id: number,
    data: IUniqLinkPayload
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.put(`admin/uniq_links/${id}`, {
            uniq_link: data,
        });
        if (response.status === 200) {
            toast.success(response.data.message || 'Cập nhật thành công');
        } else {
            toast.error(response.data.message || 'Cập nhật thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra');
        throw error;
    }
};

export const deleteUniqLink = async (
    id: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.delete(`admin/uniq_links/${id}`);
        if (response.status === 200) {
            toast.success(response.data.message || 'Xóa thành công');
        } else {
            toast.error(response.data.message || 'Xóa thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra');
        throw error;
    }
};

export const getUniqLinkInfo = async (
    id: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    const response = await axiosInstance.get(`admin/uniq_links/${id}`);
    return response.data;
};

export const downloadUniqLinkTemplate = async (
    surveyId: number
): Promise<Blob> => {
    const response = await axiosInstance.get(
        `admin/surveys/${surveyId}/user_unique_links/template`,
        {
            responseType: 'blob',
        }
    );
    return response.data;
};

export const importUniqLinks = async (
    surveyId: number,
    file: File
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axiosInstance.post(
            `admin/surveys/${surveyId}/user_unique_links/import`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        if (response.status === 200) {
            toast.success(response.data.message || 'Upload file thành công');
        } else {
            toast.error(response.data.message || 'Upload file thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra khi upload file');
        throw error;
    }
};

export const exportUniqLinks = async (surveyId: number): Promise<Blob> => {
    const response = await axiosInstance.get(
        `admin/surveys/${surveyId}/user_unique_links/export`,
        {
            responseType: 'blob',
        }
    );
    return response.data;
};

export const updateUniqLinkInSurvey = async (
    surveyId: number,
    linkId: number,
    data: {
        customer_id?: string;
        email?: string;
        phone?: string;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.put(
            `admin/surveys/${surveyId}/user_unique_links/${linkId}`,
            {
                user_survey_unique_link: data,
            }
        );
        if (response.status === 200) {
            toast.success(response.data.message || 'Cập nhật thành công');
        } else {
            toast.error(response.data.message || 'Cập nhật thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra khi cập nhật');
        throw error;
    }
};

export const deleteUniqLinkInSurvey = async (
    surveyId: number,
    linkId: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.delete(
            `admin/surveys/${surveyId}/user_unique_links/${linkId}`
        );
        if (response.status === 200) {
            toast.success(response.data.message || 'Xóa thành công');
        } else {
            toast.error(response.data.message || 'Xóa thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra khi xóa');
        throw error;
    }
};

export const sendReminderEmail = async (
    surveyId: number,
    linkId: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.post(
            `admin/surveys/${surveyId}/user_unique_links/${linkId}/send_reminder`
        );
        if (response.status === 200) {
            toast.success(response.data.message || 'Gửi email nhắc thành công');
        } else {
            toast.error(response.data.message || 'Gửi email thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra khi gửi email');
        throw error;
    }
};

export const sendBatchReminderEmail = async (
    surveyId: number,
    linkIds: number[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<any>> => {
    try {
        const response = await axiosInstance.post(
            `admin/surveys/${surveyId}/user_unique_links/send_batch_reminder`,
            {
                user_unique_link_ids: linkIds,
            }
        );
        if (response.status === 200) {
            toast.success(
                response.data.message ||
                    `Gửi email nhắc thành công cho ${linkIds.length} item`
            );
        } else {
            toast.error(response.data.message || 'Gửi email thất bại');
        }
        return response.data;
    } catch (error) {
        toast.error('Có lỗi xảy ra khi gửi email');
        throw error;
    }
};
